/**
 * Gantt auto-planning — pure functions: quantities + crew + construction start → phase date ranges.
 *
 * Offset: ±14 **calendar** days from construction start; duration spans **working days** (Mon–Fri).
 */

import { isAcquisitionClosed } from '../constants/acquisition-report.constants';
import {
  isFinanceMeterUnit,
  isFinanceStavbaVykopLine,
  isMontazZavrtPpdLine,
  isZafukWorkName,
} from '../constants/ppd-finance-categories.constants';
import {
  DEFAULT_GANTT_PLANNING_CREW_COUNTS,
  GANTT_PLANNING_ACTIVITY_IDS,
  GANTT_PLANNING_ACTIVITY_LABELS,
  GANTT_PLANNING_MAX_CREW_COUNT,
  GANTT_PLANNING_MIN_CREW_COUNT,
  GANTT_PLANNING_OFFSET_CALENDAR_DAYS,
  GANTT_PLANNING_UNIT_RATES,
  type GanttPlanningActivityId,
  type PolygonGanttPlanningCrewCounts,
} from '../constants/gantt-planning.constants';

export interface GanttPlanningPpdLineInput {
  activityName?: string | null;
  sap?: string | null;
  unit?: string | null;
  plannedQuantity?: number | null;
}

export interface GanttPlanningObjectInput {
  type: string;
  acquisitionStatusAcq?: string | null;
}

export interface GanttPlanningQuantities {
  vykopMeters: number;
  zafukTsMeters: number;
  zafukPrimOkMeters: number;
  zafukSekOkMeters: number;
  zavrtCount: number;
  odbockaCount: number;
  rozvadecCount: number;
}

export interface GanttActivitySchedule {
  activityId: GanttPlanningActivityId;
  label: string;
  plannedFrom: string;
  plannedTo: string;
  durationWorkingDays: number;
  quantity: number;
  /** Set when `metadata.ganttPlanOverrides` replaces the generated order. */
  sortOrder?: number;
  /** True when a stored override supplies the dates or the name shown on the bar. */
  manual?: boolean;
}

/** Gantt display row: planned dates from auto-planner. */
export interface GanttDisplayScheduleEntry {
  activityId: GanttPlanningActivityId;
  label: string;
  plannedFrom: string;
  plannedTo: string;
  sortKey: number;
  manual?: boolean;
}

export interface GanttPlanningInput {
  /** Construction start anchor (`expectedConstructionStart`), `dd.mm.yyyy` or ISO date prefix. */
  constructionStart: string;
  quantities: GanttPlanningQuantities;
  crew?: PolygonGanttPlanningCrewCounts | null;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addCalendarDays(date: Date, days: number): Date {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return startOfDay(d);
}

export function isWorkingDay(date: Date): boolean {
  const day = date.getDay();
  return day !== 0 && day !== 6;
}

export function snapToNextWorkingDay(date: Date): Date {
  let d = startOfDay(date);
  while (!isWorkingDay(d)) {
    d = addCalendarDays(d, 1);
  }
  return d;
}

/** Inclusive end: 1 working day → same day as start. */
export function addWorkingDaysInclusive(start: Date, workingDays: number): Date {
  if (workingDays <= 1) return startOfDay(start);
  let d = startOfDay(start);
  let counted = 1;
  while (counted < workingDays) {
    d = addCalendarDays(d, 1);
    if (isWorkingDay(d)) counted++;
  }
  return d;
}

export function parseGanttPlanningDate(value: string | null | undefined): Date | null {
  if (!value || !String(value).trim()) return null;
  const text = String(value).trim();
  const cs = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(text);
  if (cs) return validDate(Number(cs[3]), Number(cs[2]), Number(cs[1]));
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (iso) return validDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  return null;
}

function validDate(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return startOfDay(date);
}

export function formatGanttPlanningDate(date: Date): string {
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
}

export function resolveGanttPlanningCrewCounts(
  crew?: PolygonGanttPlanningCrewCounts | null,
): Required<PolygonGanttPlanningCrewCounts> {
  return {
    kopaci: sanitizeCrewCount(crew?.kopaci, DEFAULT_GANTT_PLANNING_CREW_COUNTS.kopaci),
    montaznici: sanitizeCrewCount(crew?.montaznici, DEFAULT_GANTT_PLANNING_CREW_COUNTS.montaznici),
    zafukari: sanitizeCrewCount(crew?.zafukari, DEFAULT_GANTT_PLANNING_CREW_COUNTS.zafukari),
  };
}

function sanitizeCrewCount(value: number | undefined, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  const rounded = Math.round(n);
  if (rounded < GANTT_PLANNING_MIN_CREW_COUNT || rounded > GANTT_PLANNING_MAX_CREW_COUNT) return fallback;
  return rounded;
}

export function computeGanttPlanningDurationDays(quantity: number, crewCount: number, unitRate: number): number {
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;
  const crew = Math.max(1, crewCount);
  const rate = Math.max(1, unitRate);
  const throughput = crew * rate;
  return Math.ceil(quantity / throughput);
}

function normalizePlanningText(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function classifyZafukPpdMeters(activityName: string): 'ts' | 'prim_ok' | 'sek_ok' | 'unknown' {
  const n = normalizePlanningText(activityName);
  if (!n) return 'unknown';
  if (n.includes('sekund')) return 'sek_ok';
  if (n.includes('prim')) return 'prim_ok';
  if (
    /\bts\b/.test(n) ||
    n.includes('mikrotrub') ||
    (n.includes('zafuk') && n.includes('mt'))
  ) {
    return 'ts';
  }
  return 'unknown';
}

export function extractGanttPlanningQuantitiesFromPpdLines(
  lines: ReadonlyArray<GanttPlanningPpdLineInput>,
): Pick<GanttPlanningQuantities, 'vykopMeters' | 'zafukTsMeters' | 'zafukPrimOkMeters' | 'zafukSekOkMeters'> {
  let vykopMeters = 0;
  let zafukTsMeters = 0;
  let zafukPrimOkMeters = 0;
  let zafukSekOkMeters = 0;

  for (const line of lines) {
    const qty = Number(line.plannedQuantity ?? 0);
    if (!Number.isFinite(qty) || qty <= 0) continue;
    const name = line.activityName ?? '';
    const sap = line.sap ?? '';
    const unit = line.unit ?? '';

    if (isFinanceStavbaVykopLine(name, sap, unit)) {
      vykopMeters += qty;
      continue;
    }

    if (isFinanceMeterUnit(unit) && isZafukWorkName(name)) {
      const bucket = classifyZafukPpdMeters(name);
      if (bucket === 'ts') zafukTsMeters += qty;
      else if (bucket === 'sek_ok') zafukSekOkMeters += qty;
      else zafukPrimOkMeters += qty;
      continue;
    }

    if (isMontazZavrtPpdLine(sap, name) && !isFinanceMeterUnit(unit)) {
      // závrty count comes from buildings — ignore PPD ks here
    }
  }

  return { vykopMeters, zafukTsMeters, zafukPrimOkMeters, zafukSekOkMeters };
}

export function countGanttPlanningZavrtBuildings(
  buildings: ReadonlyArray<GanttPlanningObjectInput>,
  polygonHasAcquisitionImport: boolean,
): number {
  const budovy = buildings.filter((b) => normalizeObjectType(b.type) === 'BUDOVA');
  if (budovy.length === 0) return 0;
  if (!polygonHasAcquisitionImport) return budovy.length;
  const closed = budovy.filter((b) => isAcquisitionClosed(b.acquisitionStatusAcq ?? null));
  return closed.length > 0 ? closed.length : budovy.length;
}

export function countGanttPlanningObjectsByType(
  objects: ReadonlyArray<GanttPlanningObjectInput>,
  type: 'SPOJKOVISTE' | 'ROZVADEC',
): number {
  const wanted = type;
  return objects.filter((o) => normalizeObjectType(o.type) === wanted).length;
}

function normalizeObjectType(type: string): string {
  return (type ?? '').trim().toUpperCase();
}

export function buildGanttPlanningQuantities(
  ppdLines: ReadonlyArray<GanttPlanningPpdLineInput>,
  objects: ReadonlyArray<GanttPlanningObjectInput>,
  polygonHasAcquisitionImport: boolean,
): GanttPlanningQuantities {
  const fromPpd = extractGanttPlanningQuantitiesFromPpdLines(ppdLines);
  const budovy = objects.filter((o) => normalizeObjectType(o.type) === 'BUDOVA');
  return {
    ...fromPpd,
    zavrtCount: countGanttPlanningZavrtBuildings(budovy, polygonHasAcquisitionImport),
    odbockaCount: countGanttPlanningObjectsByType(objects, 'SPOJKOVISTE'),
    rozvadecCount: countGanttPlanningObjectsByType(objects, 'ROZVADEC'),
  };
}

function activityAnchorOffsetDays(activityId: GanttPlanningActivityId): number {
  switch (activityId) {
    case 'zavrt':
    case 'zafuk_ts':
      return -GANTT_PLANNING_OFFSET_CALENDAR_DAYS;
    case 'vykop':
    case 'odbocka':
      return 0;
    case 'zafuk_prim_ok':
    case 'zafuk_sek_ok':
    case 'rozvadec':
      return GANTT_PLANNING_OFFSET_CALENDAR_DAYS;
    default:
      return 0;
  }
}

function activityQuantity(activityId: GanttPlanningActivityId, q: GanttPlanningQuantities): number {
  switch (activityId) {
    case 'zavrt':
      return q.zavrtCount;
    case 'odbocka':
      return q.odbockaCount;
    case 'rozvadec':
      return q.rozvadecCount;
    case 'vykop':
      return q.vykopMeters;
    case 'zafuk_ts':
      return q.zafukTsMeters;
    case 'zafuk_prim_ok':
      return q.zafukPrimOkMeters;
    case 'zafuk_sek_ok':
      return q.zafukSekOkMeters;
    default:
      return 0;
  }
}

function activityUnitRate(activityId: GanttPlanningActivityId): number {
  const r = GANTT_PLANNING_UNIT_RATES;
  switch (activityId) {
    case 'vykop':
      return r.kopacMetersPerDay;
    case 'zavrt':
      return r.montazZavrtPerDay;
    case 'odbocka':
      return r.montazOdbockaPerDay;
    case 'rozvadec':
      return r.montazRozvadecPerDay;
    case 'zafuk_ts':
      return r.zafukTsMetersPerDay;
    case 'zafuk_prim_ok':
    case 'zafuk_sek_ok':
      return r.zafukPrimOrSekOkMetersPerDay;
    default:
      return 1;
  }
}

function activityCrewKey(activityId: GanttPlanningActivityId): keyof Required<PolygonGanttPlanningCrewCounts> {
  switch (activityId) {
    case 'vykop':
      return 'kopaci';
    case 'zafuk_ts':
    case 'zafuk_prim_ok':
    case 'zafuk_sek_ok':
      return 'zafukari';
    default:
      return 'montaznici';
  }
}

export function computeGanttActivitySchedules(input: GanttPlanningInput): GanttActivitySchedule[] {
  const anchor = parseGanttPlanningDate(input.constructionStart);
  if (!anchor) return [];

  const crew = resolveGanttPlanningCrewCounts(input.crew);
  const schedules: GanttActivitySchedule[] = [];

  for (const activityId of Object.keys(GANTT_PLANNING_ACTIVITY_LABELS) as GanttPlanningActivityId[]) {
    const quantity = activityQuantity(activityId, input.quantities);
    const crewCount = crew[activityCrewKey(activityId)];
    const durationWorkingDays = computeGanttPlanningDurationDays(
      quantity,
      crewCount,
      activityUnitRate(activityId),
    );
    if (durationWorkingDays <= 0) continue;

    const offsetAnchor = addCalendarDays(anchor, activityAnchorOffsetDays(activityId));
    const start = snapToNextWorkingDay(offsetAnchor);
    const end = addWorkingDaysInclusive(start, durationWorkingDays);

    schedules.push({
      activityId,
      label: GANTT_PLANNING_ACTIVITY_LABELS[activityId],
      plannedFrom: formatGanttPlanningDate(start),
      plannedTo: formatGanttPlanningDate(end),
      durationWorkingDays,
      quantity,
    });
  }

  return schedules;
}

function ganttDisplayActivitySortKey(activityId: GanttPlanningActivityId): number {
  const idx = GANTT_PLANNING_ACTIVITY_IDS.indexOf(activityId);
  return idx >= 0 ? idx : GANTT_PLANNING_ACTIVITY_IDS.length;
}

/** Canonical order is spaced by 10 so a manual `sortOrder` can sit between stages. */
export function ganttScheduleSortKey(schedule: Pick<GanttActivitySchedule, 'activityId' | 'sortOrder'>): number {
  if (typeof schedule.sortOrder === 'number' && Number.isFinite(schedule.sortOrder)) return schedule.sortOrder;
  return (ganttDisplayActivitySortKey(schedule.activityId) + 1) * 10;
}

/** Gantt bars from computed schedules (ignores legacy `metadata.constructionPhasePlan`). */
export function collectGanttDisplayEntriesFromSchedules(
  schedules: ReadonlyArray<GanttActivitySchedule>,
): GanttDisplayScheduleEntry[] {
  return schedules
    .map((schedule) => ({
      activityId: schedule.activityId,
      label: schedule.label,
      plannedFrom: schedule.plannedFrom,
      plannedTo: schedule.plannedTo,
      sortKey: ganttScheduleSortKey(schedule),
      manual: schedule.manual === true,
    }))
    .sort((a, b) => a.sortKey - b.sortKey || a.activityId.localeCompare(b.activityId));
}

/** Latest planned end across schedules — useful for `plannedConstructionEnd`. */
export function maxGanttScheduleEndDate(schedules: ReadonlyArray<GanttActivitySchedule>): string | null {
  let max: Date | null = null;
  for (const s of schedules) {
    const d = parseGanttPlanningDate(s.plannedTo);
    if (!d) continue;
    if (!max || d.getTime() > max.getTime()) max = d;
  }
  return max ? formatGanttPlanningDate(max) : null;
}
